"use client";

import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/src/contexts/AuthContext";
import { useCart } from "@/src/contexts/CartContext";
import { sendOtp, verifyOtp } from "@/src/services/otp.service";
import { buyerLogin } from "@/src/services/auth.service";

interface LoginStepProps {
  outletId: string;
  onComplete: () => void;
  onClose: () => void;
}

export const LoginStep: React.FC<LoginStepProps> = ({ outletId, onComplete, onClose }) => {
  const { login } = useAuth();
  const { syncToBackend } = useCart();

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const fullPhone = `+91${phone}`;

  const handleSendOtp = async () => {
    if (phone.length !== 10) { setError("Enter a valid 10-digit phone number"); return; }
    setError("");
    setLoading(true);
    try {
      await sendOtp(fullPhone);
      setOtpSent(true);
      setResendTimer(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 3) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 4) { setError("Enter the 4-digit OTP"); return; }
    setError("");
    setLoading(true);
    try {
      await verifyOtp(fullPhone, otpValue);
      const res = await buyerLogin(phone);
      login(res.token);
      const payload = JSON.parse(atob(res.token.split(".")[1]));
      await syncToBackend(payload.userId, outletId);
      onComplete();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-[440px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Dark header */}
        <div className="bg-[#E4604E] px-8 py-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <h2 className="mt-4 text-[20px] font-bold text-white">Welcome to GrabLocal</h2>
          <p className="mt-1.5 text-sm text-white/45">Sign in to continue shopping</p>
        </div>

        {/* White body */}
        <div className="px-8 py-7">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A85]">Mobile Number</p>
          <div className="flex overflow-hidden rounded-xl border border-[#DED9CF] focus-within:border-[#E4604E]">
            <span className="flex items-center border-r border-[#DED9CF] bg-[#FAFAF8] px-4 text-sm font-semibold text-[#24242C]">
              +91
            </span>
            <input
              type="tel"
              maxLength={10}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && !otpSent && handleSendOtp()}
              placeholder="Enter your phone number"
              className="flex-1 bg-white px-4 py-3.5 text-sm outline-none placeholder-[#C0BDB7]"
            />
          </div>

          {otpSent && (
            <div className="mt-5">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A85]">Enter OTP</p>
              <div className="grid grid-cols-4 gap-3">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { otpRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="h-14 rounded-xl border-2 border-[#DED9CF] text-center text-xl font-bold text-[#24242C] outline-none transition focus:border-[#315436]"
                  />
                ))}
              </div>
            </div>
          )}

          {error && <p className="mt-3 text-sm text-[#EA5D50]">{error}</p>}

          <button
            onClick={otpSent ? handleVerify : handleSendOtp}
            disabled={loading}
            className="mt-6 w-full rounded-xl bg-[#E4604E] py-4 text-sm font-bold text-white transition hover:bg-[#C84535] disabled:opacity-50"
          >
            {loading ? "Please wait..." : otpSent ? "Verify & Continue" : "Send OTP"}
          </button>

          {otpSent && (
            <div className="mt-3 text-center">
              {resendTimer > 0 ? (
                <p className="text-xs text-[#A8A8A3]">Resend OTP in {resendTimer}s</p>
              ) : (
                <button onClick={handleSendOtp} className="text-xs font-medium text-[#315436] hover:underline">
                  Resend OTP
                </button>
              )}
            </div>
          )}

          <p className="mt-5 text-center text-xs text-[#B0ABA3]">
            By continuing, you agree to our{" "}
            <span className="font-medium text-[#315436] cursor-pointer hover:underline">Terms</span>
            {" & "}
            <span className="font-medium text-[#315436] cursor-pointer hover:underline">Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
};
